import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import type { Mark, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { HEADING_STYLES, bolderWeight, combineFontSize, resolveFontWeight } from '@/utils/rich_template_slot';
import type { RichSlotFormat, StyleMap } from '@/utils/rich_template_slot';

/**
 * The editor's side of a template slot (rich_template_slot): the content is
 * drawn in the formatting its template gives it, so it is written looking the
 * way the screen will show it.
 *
 * Nothing here is written into the content — it stays free of the template's
 * formatting, so a template edited later, or a different one picked, restyles
 * it. The formatting is laid over it as decorations that mirror, rule for
 * rule, what fillRichSlot does on the screen:
 *
 * - every run of text gets the formatting around the slot (font, size,
 *   colour, bold…), less whatever the text formats for itself — on the screen
 *   the content's own formatting sits inside the template's, so it wins;
 * - every paragraph that sets no alignment, direction or line spacing of its
 *   own takes the slot paragraph's; a paragraph on the top level takes the
 *   rest of its style too (and its size and weight, when the slot is in a
 *   heading), as it takes that paragraph's place;
 * - an empty paragraph is as tall as a line in the slot's font;
 * - the editor area takes what the blocks around the slot hand down, and
 *   Quill's .ql-editor defaults, which the screen draws every item inside.
 */

const TEMPLATE_SLOT_KEY = new PluginKey<TemplateSlotState>('templateSlot');

interface TemplateSlotState {
  format: RichSlotFormat | null;
  decorations: DecorationSet;
}

// Kept to a paragraph's own attributes on the screen, where the content's
// explicit setting wins.
const OWN_BLOCK_PROPERTIES: Record<string, string> = {
  'text-align': 'textAlign',
  'line-height': 'lineHeight',
};

const toCss = (style: StyleMap): string =>
  Object.keys(style)
    .filter((property) => style[property] !== undefined && style[property] !== '')
    .map((property) => `${property}: ${style[property]}`)
    .join('; ');

/**
 * The weight a run of text has on the screen: the weight of its block, the
 * slot's weight on that (bold is `bolder` there, a step up from what it sits
 * in), and one step more when the text is bold itself — its bold sits inside
 * the slot's. Worked out whole, because here the text's own bold sits outside
 * the decoration instead.
 */
const textWeightFor = (textblock: ProseMirrorNode, topLevel: boolean, marks: ReadonlyArray<Mark>, format: RichSlotFormat): string => {
  let base = resolveFontWeight(format.inheritedStyle['font-weight'], 400);
  if (topLevel && format.hostTag) {
    if (textblock.type.name !== 'heading') base = resolveFontWeight((HEADING_STYLES[format.hostTag] || {})['font-weight'], base);
    base = resolveFontWeight(format.blockStyle['font-weight'], base);
  }
  // A heading of the content keeps the page's heading weight — unless each
  // line becomes a list item, where it is an item like the rest.
  if (textblock.type.name === 'heading' && !flattensHeadings(topLevel, format)) {
    base = resolveFontWeight(HEADING_STYLES[`h${textblock.attrs.level}`]?.['font-weight'], base);
  }
  let weight = resolveFontWeight(format.inlineStyle['font-weight'], base);
  if (marks.some((mark) => mark.type.name === 'bold')) weight = bolderWeight(weight);
  return String(weight);
};

/** The slot's text formatting, less what the text's own marks set: those sit inside it on the screen, so they win. */
const textStyleFor = (
  marks: ReadonlyArray<Mark>,
  textblock: ProseMirrorNode,
  topLevel: boolean,
  format: RichSlotFormat
): StyleMap => {
  const style: StyleMap = { ...format.inlineStyle };
  if (style['font-weight']) style['font-weight'] = textWeightFor(textblock, topLevel, marks, format);
  marks.forEach((mark) => {
    const name = mark.type.name;
    if (name === 'textStyle') {
      if (mark.attrs.fontFamily) delete style['font-family'];
      if (mark.attrs.fontSize) {
        // The text's own size sits inside the slot's on the screen: an
        // absolute one wins outright. A relative one (1.5em, Quill's "large")
        // scales the slot's — and here it already scales whatever it sits in,
        // so a relative slot size stays as it is, and an absolute one is
        // worked out whole.
        const own = String(mark.attrs.fontSize);
        const slotSize = style['font-size'];
        if (!slotSize || !/(em|%)$/.test(own)) delete style['font-size'];
        else if (!/(em|%)$/.test(slotSize)) style['font-size'] = combineFontSize(slotSize, own);
      }
      if (mark.attrs.color) delete style.color;
    } else if (name === 'highlight') {
      delete style['background-color'];
    } else if (name === 'italic') {
      delete style['font-style'];
    } else if (name === 'link') {
      // A link keeps the page's link colour, on the screen as here.
      delete style.color;
    } else if (name === 'code') {
      delete style['font-family'];
    }
  });
  return style;
};

// Where each line of the content becomes a list item of its own, a heading of
// the content is an item like the rest on the screen.
const flattensHeadings = (topLevel: boolean, format: RichSlotFormat): boolean =>
  topLevel && Boolean(format.list && format.list.itemPerLine);

// A list item's marker, as Quill draws it: a bullet, or the item's number.
const listMarker = (format: RichSlotFormat, number: number): string =>
  format.list && format.list.ordered ? `"${number}. "` : '"\\2022"';

/**
 * A line's height, as the screen gives an empty one (a line break in the
 * slot's formatting): its font size at the line height it sits in.
 */
const emptyLineHeight = (format: RichSlotFormat): string | null => {
  const size = format.inlineStyle['font-size'];
  if (!size) return null;
  const lineHeight =
    format.inlineStyle['line-height'] || format.blockStyle['line-height'] || format.inheritedStyle['line-height'] || '1.42';
  return /^\d*\.?\d+$/.test(lineHeight) ? `calc(${size} * ${lineHeight})` : lineHeight;
};

/**
 * What a line takes when it sets nothing of its own, beyond the slot line's
 * attributes: in a one-item list the display lifts the first line's alignment
 * and direction onto the item (see rich_template_slot liftToItem), so the
 * lines after it follow the first.
 */
interface LineDefaults {
  textAlign: string | null;
  dir: string | null;
}

/** A list item's marker for a line: its text, and whether the item stands right to left. */
interface LineMarker {
  text: string;
  rtl: boolean;
}

const blockAttributesFor = (
  node: ProseMirrorNode,
  topLevel: boolean,
  format: RichSlotFormat,
  marker: LineMarker | null,
  lifted: LineDefaults | null = null
): Record<string, string> | null => {
  const style: StyleMap = {};
  const attributes: Record<string, string> = {};
  const classes: Array<string> = [];

  if (format.hostTag) {
    if (topLevel) {
      // The paragraph takes the slot paragraph's place — a heading's size,
      // weight and line height included.
      if (node.type.name !== 'heading') Object.assign(style, HEADING_STYLES[format.hostTag] || {});
      // A heading of the content made a list item: the item's own size,
      // weight and spacing — then the item's style, as for any line.
      if (node.type.name === 'heading' && flattensHeadings(topLevel, format)) {
        Object.assign(style, { 'font-size': '1em', 'font-weight': 'inherit', 'line-height': 'inherit' });
      }
      Object.assign(style, format.blockStyle);
    } else {
      Object.keys(OWN_BLOCK_PROPERTIES).forEach((property) => {
        if (format.blockStyle[property]) style[property] = format.blockStyle[property];
      });
    }
    if (lifted && topLevel && !style['text-align'] && lifted.textAlign) style['text-align'] = lifted.textAlign;
    Object.keys(OWN_BLOCK_PROPERTIES).forEach((property) => {
      if (node.attrs[OWN_BLOCK_PROPERTIES[property]]) delete style[property];
    });
    const dir = format.blockDir || (lifted && topLevel ? lifted.dir : null);
    if (dir && !node.attrs.textDirection) attributes.dir = dir;
  }

  // The list item's marker beside the line, as Quill draws it on the screen —
  // on a line of its own above a first line that is not a paragraph, as the
  // display leaves it there.
  if (marker) {
    // (A heading made a list item has its marker beside it like any line.)
    const alone = node.type.name !== 'paragraph' && !(format.list && format.list.itemPerLine);
    classes.push(alone ? 'rte-slot-marker-alone' : 'rte-slot-marker');
    if (marker.rtl) classes.push('rte-slot-marker--rtl');
    style['--rte-slot-marker'] = marker.text;
    // Each line an item of its own: the item's indent, on its side (the
    // list's own is the editor area's).
    if (format.list && format.list.itemPerLine) style[marker.rtl ? 'margin-right' : 'margin-left'] = '1.5em';
    // A blank marked line: as tall as a line in the slot's font.
    const height = node.content.size === 0 ? emptyLineHeight(format) : null;
    if (height) style['min-height'] = height;
  }

  // An empty line: on the screen it holds a line break in the slot's
  // formatting, so it is as tall as a line of it.
  if (node.content.size === 0) {
    const sizing: Array<[string, string]> = [
      ['--rte-slot-font-size', format.inlineStyle['font-size']],
      ['--rte-slot-font-family', format.inlineStyle['font-family']],
      ['--rte-slot-line-height', format.inlineStyle['line-height']],
    ];
    if (sizing.some(([, value]) => value)) {
      classes.push('rte-slot-empty');
      sizing.forEach(([property, value]) => {
        if (value) style[property] = value;
      });
    }
  }

  if (classes.length) attributes.class = classes.join(' ');
  const css = toCss(style);
  if (css) attributes.style = css;
  return Object.keys(attributes).length ? attributes : null;
};

/**
 * Where an image in its own line sits: the screen puts it in a line of the
 * template's, so it takes that line's alignment (Quill's .ql-editor aligns
 * left where the template says nothing).
 */
const imageAttributesFor = (format: RichSlotFormat): Record<string, string> | null => {
  if (!format.hostTag) return null;
  let align = format.blockStyle['text-align'] || format.inheritedStyle['text-align'] || '';
  if (align === 'justify') align = (format.blockDir || format.inheritedDir) === 'rtl' ? 'right' : 'left';
  if (align === 'center') return { style: 'display: block; margin-left: auto; margin-right: auto' };
  if (align === 'right') return { style: 'display: block; margin-left: auto; margin-right: 0' };
  return null;
};

/** The decorations that draw a document in its slot's formatting. */
export const templateSlotDecorations = (doc: ProseMirrorNode, format: RichSlotFormat | null): DecorationSet => {
  if (!format) return DecorationSet.empty;
  const decorations: Array<Decoration> = [];
  // The paragraph being walked: its text comes straight after it.
  let textblock: ProseMirrorNode | null = null;
  let topLevel = false;
  // Lines marked as list items so far, for their numbers.
  let marked = 0;
  const list = format.list;
  // A one-item list: the first line's alignment and direction, for the rest.
  let lifted: LineDefaults | null = null;

  doc.descendants((node, pos, parent) => {
    // Code keeps its own look — the screen leaves code blocks as written.
    if (node.type.spec.code) return false;
    if (node.type.name === 'image' && parent === doc) {
      const attributes = imageAttributesFor(format);
      if (attributes) decorations.push(Decoration.node(pos, pos + node.nodeSize, attributes));
      return false;
    }
    // A first block that is not a line (a list, a quote) in a one-item list:
    // the item's marker stands above it, as on the screen.
    if (list && list.marker && !list.itemPerLine && parent === doc && doc.firstChild === node && !node.isTextblock) {
      decorations.push(Decoration.node(pos, pos + node.nodeSize, {
        class: `rte-slot-marker-alone${singleItemIsRtl(doc, format) ? ' rte-slot-marker--rtl' : ''}`,
        style: `--rte-slot-marker: ${listMarker(format, list.start)}`,
      }));
      marked += 1;
    }
    if (node.isTextblock) {
      textblock = node;
      topLevel = parent === doc;
      // Each line an item of its own, or only the first beside the item's marker.
      const marks = Boolean(list && list.marker && topLevel && (list.itemPerLine || (marked === 0 && doc.firstChild === node)));
      const marker: LineMarker | null =
        marks && list
          ? {
              text: listMarker(format, list.start + marked),
              rtl: list.itemPerLine ? lineIsRtl(node, format) : singleItemIsRtl(doc, format),
            }
          : null;
      if (marks) marked += 1;
      const attributes = blockAttributesFor(node, topLevel, format, marker, topLevel && doc.firstChild !== node ? lifted : null);
      if (attributes) decorations.push(Decoration.node(pos, pos + node.nodeSize, attributes));
      if (list && !list.itemPerLine && topLevel && doc.firstChild === node) {
        lifted = {
          textAlign: node.attrs.textAlign || format.blockStyle['text-align'] || null,
          dir: node.attrs.textDirection || format.blockDir || null,
        };
      }
      return true;
    }
    if (node.isText && textblock) {
      const css = toCss(textStyleFor(node.marks, textblock, topLevel, format));
      if (css) decorations.push(Decoration.inline(pos, pos + node.nodeSize, { style: css }));
      return false;
    }
    return true;
  });

  return DecorationSet.create(doc, decorations);
};

/* ---------------------------------- colour --------------------------------- */

let colorContext: CanvasRenderingContext2D | null | undefined;

const channelsToHex = (red: number, green: number, blue: number): string =>
  `#${[red, green, blue].map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0')).join('')}`;

/** Any CSS colour as "#rrggbb" — what a colour input takes — or null when it is not one (or is transparent). */
export const cssColorToHex = (value: string | null | undefined): string | null => {
  const text = String(value || '').trim();
  if (!text) return null;

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].replace(/./g, (digit) => digit + digit) : hex[1];
    return `#${digits.toLowerCase()}`;
  }
  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/i.exec(text);
  if (rgb) {
    const alpha = rgb[4] === undefined ? 1 : rgb[4].endsWith('%') ? parseFloat(rgb[4]) / 100 : Number(rgb[4]);
    return alpha === 0 ? null : channelsToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
  }

  // Named colours and the rest: the browser resolves them.
  if (colorContext === undefined) {
    try {
      colorContext = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    } catch (error) {
      colorContext = null;
    }
  }
  if (!colorContext) return null;
  const unset = '#010203';
  colorContext.fillStyle = unset;
  colorContext.fillStyle = text;
  const resolved = String(colorContext.fillStyle);
  if (resolved === unset && text.toLowerCase() !== unset) return null;
  return resolved.startsWith('#') ? resolved : cssColorToHex(resolved);
};

const luminance = (hex: string): number => {
  const [red, green, blue] = [1, 3, 5].map((index) => {
    const channel = parseInt(hex.slice(index, index + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

const contrast = (first: string, second: string): number => {
  const [light, dark] = [luminance(first), luminance(second)].sort((a, b) => b - a);
  return (light + 0.05) / (dark + 0.05);
};

// The editor's own page colour, and the one it switches to for text too light
// to read on it (a template's white or gold for a dark screen).
const EDITOR_BACKGROUND = '#ffffff';
const DARK_BACKGROUND = '#1f2937';

/* ------------------------------- editor area ------------------------------- */

// A line's direction on the screen: its own, else the slot line's.
const lineIsRtl = (node: ProseMirrorNode, format: RichSlotFormat): boolean =>
  (node.attrs.textDirection || format.blockDir) === 'rtl';

/**
 * Whether the one list item of a list template stands right to left: the
 * template's item says so, or — setting nothing — it takes its first line's
 * direction on the screen (rich_template_slot liftToItem).
 */
const singleItemIsRtl = (doc: ProseMirrorNode | null, format: RichSlotFormat): boolean => {
  if (!format.list) return false;
  if (format.list.rtl) return true;
  const first = doc ? doc.firstChild : null;
  if (first && !first.isTextblock) return false;
  return (first ? first.attrs.textDirection || format.blockDir : format.blockDir) === 'rtl';
};

/** The editor area's style: what the blocks around the slot hand down, on Quill's .ql-editor defaults. */
export const templateSlotCanvasStyle = (format: RichSlotFormat | null, doc: ProseMirrorNode | null = null): StyleMap => {
  if (!format) return {};
  const style: StyleMap = {
    'text-align': 'left',
    'line-height': '1.42',
    ...format.inheritedStyle,
  };
  if (format.inheritedDir === 'rtl' || format.inheritedDir === 'ltr') style.direction = format.inheritedDir;
  // A slot in a list item: indented as Quill indents lists and their items
  // (1.5em each; a right-to-left Quill item takes its own on the right), on
  // top of the editor's own padding.
  if (format.list) {
    if (format.list.itemPerLine) {
      // Each line is an item and brings its own indent (blockAttributesFor).
      style['padding-left'] = 'calc(14px + 1.5em)';
    } else if (singleItemIsRtl(doc, format)) {
      style['padding-left'] = 'calc(14px + 1.5em)';
      style['padding-right'] = 'calc(14px + 1.5em)';
    } else {
      style['padding-left'] = 'calc(14px + 3em)';
    }
    // The item's own type, for a marker drawn inside a heading (see
    // .rte-slot-marker-alone): the list's size where it is absolute.
    const itemSize = format.inheritedStyle['font-size'];
    if (itemSize && /(px|rem)$/.test(itemSize)) style['--rte-slot-item-font-size'] = itemSize;
    style['--rte-slot-item-line-height'] = format.inheritedStyle['line-height'] || '1.42';
  }

  if (!style['background-color']) {
    const textColor =
      cssColorToHex(format.inlineStyle.color || format.blockStyle.color || format.inheritedStyle.color) || '#111827';
    const behindText = format.inlineStyle['background-color'] || format.blockStyle['background-color'];
    if (!behindText && contrast(textColor, EDITOR_BACKGROUND) < 2) style['background-color'] = DARK_BACKGROUND;
  }
  return style;
};

/* --------------------------------- toolbar --------------------------------- */

export interface TemplateSlotDefaults {
  fontFamily: string;
  /** In pixels, as the size menu lists it; '' when the template sets none (or not in pixels). */
  fontSize: string;
  color: string | null;
  highlight: string | null;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  textAlign: string;
  direction: string;
  lineHeight: string;
}

const NO_DEFAULTS: TemplateSlotDefaults = {
  fontFamily: '',
  fontSize: '',
  color: null,
  highlight: null,
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  textAlign: '',
  direction: '',
  lineHeight: '',
};

/**
 * What the content's text looks like where it sets nothing itself — shown on
 * the toolbar, so its font menu reads the template's font rather than a blank.
 */
export const templateSlotDefaults = (format: RichSlotFormat | null): TemplateSlotDefaults => {
  if (!format) return NO_DEFAULTS;
  const heading = format.hostTag ? HEADING_STYLES[format.hostTag] || {} : {};
  const layers: Array<StyleMap> = [format.inheritedStyle, heading, format.blockStyle, format.inlineStyle];
  const pick = (property: string): string =>
    layers.reduce((value, layer) => (layer[property] && layer[property] !== 'inherit' ? layer[property] : value), '');

  const fontSize = layers.reduce<string | undefined>(
    (size, layer) => (layer['font-size'] ? combineFontSize(size, layer['font-size']) : size),
    undefined
  );
  const pixels = /^(\d*\.?\d+)px$/.exec(fontSize || '');
  const weight = pick('font-weight');
  const decoration = layers.map((layer) => layer['text-decoration-line'] || '').join(' ');

  return {
    fontFamily: pick('font-family'),
    fontSize: pixels ? String(Number(pixels[1])) : '',
    color: cssColorToHex(pick('color')),
    highlight: cssColorToHex(format.inlineStyle['background-color']),
    bold: weight === 'bold' || weight === 'bolder' || Number(weight) >= 600,
    italic: /italic|oblique/.test(pick('font-style')),
    underline: /underline/.test(decoration),
    strike: /line-through/.test(decoration),
    textAlign: format.blockStyle['text-align'] || format.inheritedStyle['text-align'] || '',
    direction: format.blockDir || format.inheritedDir || '',
    lineHeight: format.blockStyle['line-height'] || '',
  };
};

/* -------------------------------- extension -------------------------------- */

/**
 * The editor extension. Configure with `format` for the first draw; later
 * changes go through setTemplateSlotFormat.
 */
export const TemplateSlot = Extension.create<{ format: RichSlotFormat | null }>({
  name: 'templateSlot',

  addOptions() {
    return { format: null };
  },

  addProseMirrorPlugins() {
    const initialFormat = this.options.format;
    return [
      new Plugin<TemplateSlotState>({
        key: TEMPLATE_SLOT_KEY,
        state: {
          init: (_config, state) => ({
            format: initialFormat,
            decorations: templateSlotDecorations(state.doc, initialFormat),
          }),
          apply: (transaction, previous, _oldState, newState) => {
            const next = transaction.getMeta(TEMPLATE_SLOT_KEY) as { format: RichSlotFormat | null } | undefined;
            const format = next ? next.format : previous.format;
            if (!next && !transaction.docChanged) return previous;
            return { format, decorations: templateSlotDecorations(newState.doc, format) };
          },
        },
        props: {
          decorations: (state) => TEMPLATE_SLOT_KEY.getState(state)?.decorations,
          attributes: (state): Record<string, string> => {
            const format = TEMPLATE_SLOT_KEY.getState(state)?.format || null;
            if (!format) return {};
            return { class: 'rte-slot', style: toCss(templateSlotCanvasStyle(format, state.doc)) };
          },
        },
      }),
    ];
  },
});

/** Redraws the editor in a new slot formatting (null: none). The content itself is left as it is. */
export const setTemplateSlotFormat = (editor: Editor, format: RichSlotFormat | null): void => {
  if (!editor || editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(TEMPLATE_SLOT_KEY, { format }));
};
